import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { API_BASE_URL } from '../config';
import AdminLogin from './AdminLogin';
import { calculateAge, formatBirthDate } from '../utils/ageCalculator';

interface UserStatistics {
  id: string;
  username: string;
  email: string;
  created_at: string;
  last_active: string | null;
  last_tracked: string | null;
  total_trackings: number;
  num_babies: number;
}

interface BabyStatistics {
  id: string;
  name: string;
  birth_date: string | null;
  created_at: string;
  num_trackings: number;
}

type SortField = 'username' | 'email' | 'created_at' | 'last_active' | 'last_tracked' | 'total_trackings' | 'num_babies';
type SortDirection = 'asc' | 'desc';

const Admin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState<UserStatistics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('last_active');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [userBabies, setUserBabies] = useState<Map<string, BabyStatistics[]>>(new Map());
  const [loadingBabies, setLoadingBabies] = useState<Set<string>>(new Set());

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setUsers([]);
  }, []);

  const fetchUsers = useCallback(async (credentials?: string) => {
    setLoading(true);
    setError('');

    try {
      const auth = credentials || sessionStorage.getItem('adminAuth');
      if (!auth) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    // Check if already authenticated
    const adminAuth = sessionStorage.getItem('adminAuth');
    if (adminAuth) {
      setIsAuthenticated(true);
      fetchUsers(adminAuth);
    }
  }, [fetchUsers]);

  const handleDeleteUser = async (userId: string, username: string) => {
    if (!window.confirm(`Delete user "${username}" and all their data?\n\nThis will permanently delete:\n- User account\n- All babies\n- All sleep entries\n- All links and invites\n\nThis action cannot be undone!`)) {
      return;
    }

    try {
      const auth = sessionStorage.getItem('adminAuth');
      if (!auth) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error('Session expired. Please login again.');
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete user');
      }

      const result = await response.json();
      console.log('User deleted:', result);

      // Refresh the user list
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleUserExpansion = async (userId: string) => {
    const newExpanded = new Set(expandedUsers);

    if (newExpanded.has(userId)) {
      // Collapse
      newExpanded.delete(userId);
      setExpandedUsers(newExpanded);
    } else {
      // Expand
      newExpanded.add(userId);
      setExpandedUsers(newExpanded);

      // Fetch babies if not already loaded
      if (!userBabies.has(userId)) {
        await fetchUserBabies(userId);
      }
    }
  };

  const fetchUserBabies = async (userId: string) => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) return;

    setLoadingBabies(new Set(loadingBabies).add(userId));

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/babies`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });

      if (response.ok) {
        const babies = await response.json();
        setUserBabies(new Map(userBabies).set(userId, babies));
      }
    } catch (err) {
      console.error('Failed to fetch user babies:', err);
    } finally {
      const newLoading = new Set(loadingBabies);
      newLoading.delete(userId);
      setLoadingBabies(newLoading);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];

    // Handle null values
    if (aVal === null) aVal = sortDirection === 'asc' ? Infinity : -Infinity;
    if (bVal === null) bVal = sortDirection === 'asc' ? Infinity : -Infinity;

    // Convert dates to timestamps for comparison
    if (sortField === 'created_at' || sortField === 'last_active' || sortField === 'last_tracked') {
      aVal = aVal === -Infinity || aVal === Infinity ? aVal : new Date(aVal).getTime();
      bVal = bVal === -Infinity || bVal === Infinity ? bVal : new Date(bVal).getTime();
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-header-actions">
          <button onClick={() => fetchUsers()} className="btn-refresh" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {users.reduce((sum, user) => sum + user.total_trackings, 0)}
          </div>
          <div className="stat-label">Total Sleep Entries</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {users.reduce((sum, user) => sum + user.num_babies, 0)}
          </div>
          <div className="stat-label">Total Babies</div>
        </div>
      </div>

      <div className="admin-controls">
        <input
          type="text"
          placeholder="Search by username or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('username')} className="sortable">
                Username {sortField === 'username' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                Email {sortField === 'email' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Created {sortField === 'created_at' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('last_active')} className="sortable">
                Last Active {sortField === 'last_active' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('last_tracked')} className="sortable">
                Last Tracked {sortField === 'last_tracked' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('total_trackings')} className="sortable">
                Trackings {sortField === 'total_trackings' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('num_babies')} className="sortable">
                Babies {sortField === 'num_babies' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(user => (
              <React.Fragment key={user.id}>
                <tr className={expandedUsers.has(user.id) ? 'expanded' : ''}>
                  <td>
                    <span
                      className="expand-btn"
                      onClick={() => toggleUserExpansion(user.id)}
                      title="Show babies"
                    >
                      {expandedUsers.has(user.id) ? '▼' : '▶'}
                    </span>
                    {user.username}
                  </td>
                  <td>{user.email}</td>
                  <td title={formatDate(user.created_at)}>
                    {formatRelativeTime(user.created_at)}
                  </td>
                  <td title={formatDate(user.last_active)}>
                    {formatRelativeTime(user.last_active)}
                  </td>
                  <td title={formatDate(user.last_tracked)}>
                    {formatRelativeTime(user.last_tracked)}
                  </td>
                  <td>{user.total_trackings}</td>
                  <td>{user.num_babies}</td>
                  <td>
                    <button
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      className="btn-delete"
                      title="Delete user and all associated data"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {expandedUsers.has(user.id) && (
                  <tr className="baby-details-row">
                    <td colSpan={8}>
                      <div className="baby-details">
                        <h4>Babies:</h4>
                        {loadingBabies.has(user.id) ? (
                          <p className="loading-babies">Loading babies...</p>
                        ) : userBabies.get(user.id)?.length === 0 ? (
                          <p className="no-babies">No babies</p>
                        ) : (
                          <table className="babies-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Birth Date</th>
                                <th>Age</th>
                                <th>Trackings</th>
                                <th>Added</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userBabies.get(user.id)?.map(baby => (
                                <tr key={baby.id}>
                                  <td>{baby.name}</td>
                                  <td>{formatBirthDate(baby.birth_date)}</td>
                                  <td>{calculateAge(baby.birth_date)}</td>
                                  <td>{baby.num_trackings}</td>
                                  <td>{new Date(baby.created_at).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {sortedUsers.length === 0 && !loading && (
        <div className="no-results">
          {searchTerm ? 'No users found matching your search' : 'No users yet'}
        </div>
      )}
    </div>
  );
};

export default Admin;
